/*
 * Behaviour shared across every page.
 *
 * Deliberately small and dependency free. Larger component controllers live
 * in explicit assets and are loaded only when their markup needs them.
 */

(() => {
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------
     Scroll reveal

     Elements marked .reveal start slightly low and settle when they first
     come into view. Text stays fully opaque so the transition never blends a
     reviewed colour below its contrast floor. Once only: re-animating
     something on the way back up is nausea, not delight.

     The CSS carries the whole animation, so if this script never runs
     the .no-js rule leaves everything visible.
     --------------------------------------------------------------- */

  const initReveal = (scope = document) => {
    const targets = scope.querySelectorAll('.reveal:not(.is-revealed)');
    if (!targets.length) return;

    if (reduced() || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.3 }
    );

    targets.forEach((el) => {
      el.classList.add('is-reveal-ready');
      observer.observe(el);
    });
  };

  initReveal();

  /* ---------------------------------------------------------------
     Image placeholders

     Every media image pulses a coloured block until its bitmap has
     decoded. The CSS does the pulsing; all this has to do is say when
     to stop, which the photograph covering the block would not do on
     its own for anything with transparency or letterboxing.

     Marked broken rather than loaded on error, which puts the alt text
     back — a missing photo should still say what it was of.
     --------------------------------------------------------------- */
  const SKELETON = '.product-card__img, .predictive__thumb, .cart__thumb img, .product__media img';

  const settle = (img) => {
    img.classList.remove('is-loading');
    img.classList.add(img.naturalWidth === 0 ? 'is-broken' : 'is-loaded');
  };

  const initImages = (scope = document) => {
    for (const img of scope.querySelectorAll(SKELETON)) {
      if (img.classList.contains('is-loaded') || img.classList.contains('is-broken')) continue;
      // `complete` covers images already in the browser cache, which never
      // fire load and would otherwise pulse forever.
      if (img.complete) settle(img);
      else {
        // Native lazy images stay on a static placeholder while offscreen.
        // Only eagerly requested media earns an animated loading treatment.
        if (img.loading !== 'lazy') img.classList.add('is-loading');
        img.addEventListener('load', () => settle(img), { once: true });
        img.addEventListener('error', () => settle(img), { once: true });
      }
    }
  };

  initImages();

  /*
   * The theme editor rebuilds a section's DOM on every edit, so anything
   * initialised at load has to be initialised again for the new markup.
   * The collection grid and the search panel swap markup for the same reason.
   */
  const reinit = (scope) => {
    initReveal(scope);
    initImages(scope);
  };

  document.addEventListener('shopify:section:load', (event) => reinit(event.target));
  document.addEventListener('catalogue:updated', (event) => reinit(event.target));
  document.addEventListener('theme:images:added', (event) => initImages(event.target));

  /* Predictive search is a convenience, so defer its controller until use. */
  let predictiveLoading = false;
  const loadPredictiveSearch = (event) => {
    const root = event.target.closest?.('[data-predictive-search]');
    const source = root?.dataset.controllerSrc;
    if (
      !root ||
      !source ||
      predictiveLoading ||
      !('customElements' in window) ||
      customElements.get('predictive-search')
    ) return;

    predictiveLoading = true;
    const script = document.createElement('script');
    script.src = source;
    script.async = true;
    script.addEventListener('error', () => {
      predictiveLoading = false;
    });
    document.head.append(script);
  };

  document.addEventListener('focusin', loadPredictiveSearch);
  document.addEventListener('input', loadPredictiveSearch);
  document.addEventListener('pointerdown', loadPredictiveSearch, { passive: true });
})();
